import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Button, CircularProgress } from '@material-ui/core';
import useStyles from './ItemContent.styles';
import useApi from '../../utils/useApi';
import { endpoints } from '../../utils/endpoints';
import { IEditForm } from '../../types/interfaces/IEditForm';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import tPaths from '../../utils/tPaths';
import { IKeyLabel } from '../../types/interfaces/IKeyLabel';
import InputFields from './InputFields/InputFields';
import SwitchInput from '../SwitchInput/SwitchInput';
import { useCallbackOne, useMemoOne } from 'use-memo-one';
import useSnackbar from '../../utils/useSnackbar';
import { downloadFile } from '../../utils/downloadUtil';
import _ from 'lodash';
import { TRY } from '../../utils/TRY';
import { ETables } from '../../types/enums/ETables';
import { EPaths } from '../../types/enums/EPaths';

interface IFieldError {
  field: string;
  errorText: string;
}

interface ITabFieldsError {
  tab: string;
  fieldsErrors: IFieldError[];
}

export interface IValidationResponse {
  tabsErrors?: ITabFieldsError[];
  fieldsErrors?: IFieldError[];
}

export interface IOnChangeInputs {
  fieldName: any;
  value: any;
  tab?: any;
}

interface IProps {
  path: string;
  title?: string;
  hint?: (data?: any) => string;
  itemId?: string;
  updateDataFormatter?: (fields: any, data: any) => any;
  contentMapper?: (data?: any) => IEditForm[][];
  tabs?: IKeyLabel[];
  tabsContentMapper?: (data?: any) => Record<string, IEditForm[][]>;
  switches?: IKeyLabel[];
  switchesDataMapper?: (data: any) => Record<string, boolean>;
  editable?: boolean;
  addItem?: boolean;
  backButton?: boolean;
  apiUrl?: string;
  onInputValidation?: (form: any) => IValidationResponse | void;
  onChangeFormatter?: (props: IOnChangeInputs) => any;
  downloadPdf?: boolean;
}

const ItemContent: React.FC<IProps> = ({
  path,
  title,
  hint,
  itemId,
  contentMapper,
  tabs,
  tabsContentMapper,
  updateDataFormatter,
  switches,
  switchesDataMapper,
  editable = true,
  addItem = false,
  backButton = true,
  downloadPdf = false,
  apiUrl,
  onInputValidation,
  onChangeFormatter,
}) => {
  const fetchUrl = useMemoOne(() => {
    if (addItem) {
      return null;
    }
    if (apiUrl) {
      return apiUrl;
    }
    return endpoints.tableItem(path, itemId);
  }, [addItem, apiUrl, path]);

  const { data, post, patch, get, mutate } = useApi<any>(fetchUrl);
  const styles = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState<string | undefined>(tabs ? tabs[0].key : undefined);
  const [switchesStates, setSwitchesStates] = useState<Record<string, boolean> | undefined>(
    addItem ? switches?.reduce((acc, cv) => ({ ...acc, [cv.key]: true }), {}) : undefined,
  );

  const [fields, setFields] = useState<any>(undefined);

  const pushSnack = useSnackbar();

  const handleOnClickSave = useCallback(() => {
    if (!updateDataFormatter) {
      throw new Error('updateDataFormatter is not defined');
    }

    const [error, formattedData] = TRY(() => updateDataFormatter({ ...fields, ...switchesStates }, data));

    if (error) {
      pushSnack({ message: error.message, severity: 'error' });
      return;
    }
    if (onInputValidation) {
      const errors = onInputValidation(fields);

      if (errors) {
        const { fieldsErrors, tabsErrors } = errors;
        if (tabsErrors) {
          setFields((cv: any) => ({
            ...cv,
            ...tabsErrors.reduce(
              (acc, { fieldsErrors, tab }) => ({
                ...acc,
                [tab]: {
                  ...cv[tab],
                  ...fieldsErrors.reduce(
                    (acc, { errorText, field }) => ({
                      ...acc,
                      [field]: { ...fields[field], error: true, errorText },
                    }),
                    {},
                  ),
                },
              }),
              {},
            ),
          }));
          return;
        }

        if (fieldsErrors) {
          setFields((cv: any) => ({
            ...cv,
            ...fieldsErrors.reduce(
              (acc, { errorText, field }) => ({
                ...acc,
                [field]: { ...fields[field], error: true, errorText },
              }),
              {},
            ),
          }));
          return;
        }
      }
    }

    if (addItem) {
      post({
        endpoint: apiUrl || path,
        body: formattedData,
        onSuccess: () => {
          pushSnack({ message: t(tPaths.itemContent.successOnAdd) });
          history.goBack();
        },
        onError: (error) => {
          const firstInvalidProperty = _.get(error?.response, 'data.errors[0].constraints', {});

          pushSnack({ message: Object.values(firstInvalidProperty).join(), severity: 'error' });
        },
      });

      return;
    }

    patch({
      endpoint: fetchUrl || endpoints.tableItem(path, itemId),
      body: formattedData,
      onSuccess: (response) => {
        pushSnack({ message: t(tPaths.itemContent.successOnEdit) });
        mutate(response?.data);
      },
      onError: (error) => {
        let message = null;

        const firstInvalidProperty = _.get(error?.response, 'data.errors[0].constraints', {});
        message = Object.values(firstInvalidProperty).join('').trim();

        if (!message) message = error?.response?.data?.message;

        if (!message) message = '';

        pushSnack({ message, severity: 'error' });
      },
    });
  }, [
    fetchUrl,
    updateDataFormatter,
    fields,
    switchesStates,
    data,
    onInputValidation,
    addItem,
    patch,
    apiUrl,
    path,
    itemId,
    post,
    pushSnack,
    t,
    history,
  ]);

  const handleOnClickBack = useCallback(
    () => history.push(path === ETables.InAppNotifications ? EPaths.InAppNotifications : path),
    [history, path],
  );

  const content = useMemo(
    () => ((data && contentMapper) || (addItem && contentMapper) ? contentMapper(data) : undefined),
    [data, contentMapper, addItem],
  );

  const tabsContent = useMemo(
    () => ((data && tabsContentMapper) || (addItem && tabsContentMapper) ? tabsContentMapper(data) : undefined),
    [addItem, data, tabsContentMapper],
  );

  useEffect(() => {
    if (data && switches && switchesDataMapper) {
      setSwitchesStates(switchesDataMapper(data));
    }
  }, [data, switches, switchesDataMapper]);

  useEffect(() => {
    if ((!fields && data) || (!fields && addItem)) {
      if (tabsContent && tabs) {
        setFields(
          tabs.reduce(
            (acc, cv) => ({
              ...acc,
              [cv.key]: tabsContent[cv.key].flat(1).reduce(
                (acc, cv) => ({
                  ...acc,
                  [cv.fieldName]: { value: cv.initialValue },
                }),
                {},
              ),
            }),
            {},
          ),
        );
      }
      if (content) {
        setFields(
          content.flat(1).reduce(
            (acc, cv) => ({
              ...acc,
              [cv.fieldName]: { value: cv.initialValue },
            }),
            {},
          ),
        );
      }
    }
  }, [addItem, content, data, fields, tabs, tabsContent]);

  const setInputState = useCallback((fieldName, value, tab?: string) => {
    if (tab) {
      setFields((cv: any) => ({
        ...cv,
        [tab]: { ...cv[tab], [fieldName]: { value, error: undefined, errorText: undefined } },
      }));
    } else {
      setFields((cv: any) => ({
        ...cv,
        [fieldName]: { value, error: undefined, errorText: undefined },
      }));
    }
  }, []);

  const handleInputChange = useCallback(
    (fieldName, value, tab?: string) => {
      if (onChangeFormatter) {
        const formattedValue = onChangeFormatter({ fieldName, value, tab });
        setInputState(fieldName, formattedValue, tab);
      } else {
        setInputState(fieldName, value, tab);
      }
    },
    [onChangeFormatter, setInputState],
  );

  const handleSwitchToggle = (key: string) => {
    setSwitchesStates((cv) => (cv ? { ...cv, [key]: !cv[key] } : cv));
  };

  const download = useCallbackOne(() => {
    get({
      endpoint: '/v1/packages/purchaseReceipt/pdf',
      options: {
        responseType: 'blob',
      },
      onSuccess: ({ data }) => downloadFile(data, { name: 'Purchase receipt', extension: 'pdf' }),
    });
  }, []);

  return (
    <Box width="fit-content">
      {fields ? (
        <Box display="flex" flexDirection="column">
          <Box className={styles.headerContainer}>
            <Typography variant="h4">{title}</Typography>
            {hint && <span className={styles.hint}>{hint(data)}</span>}

            {downloadPdf && (
              <Box marginLeft="auto" display="flex">
                <Button onClick={download}>{t(tPaths.itemContent.downloadPdf)}</Button>
              </Box>
            )}

            {switches && (
              <Box className={styles.switchContainer}>
                {switches.map((value) => (
                  <SwitchInput
                    onChange={(fieldName, value) => handleSwitchToggle(fieldName)}
                    label={value.label}
                    value={switchesStates ? switchesStates[value.key] : undefined}
                    fieldName={value.key}
                    type="switch"
                    key={value.key}
                  />
                ))}
              </Box>
            )}
          </Box>
          <Box className={styles.container}>
            {tabs ? (
              <>
                <Box className={styles.tabBar}>
                  {tabs.map((tab) => (
                    <Box
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={activeTab === tab.key ? styles.tabActive : styles.tabInactive}
                    >
                      <Typography
                        variant="subtitle1"
                        className={activeTab === tab.key ? styles.tabActiveTypography : styles.tabInactiveTypography}
                      >
                        {tab.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Box className={styles.description}>
                  {tabs.map(
                    (tab) =>
                      activeTab === tab.key &&
                      tabsContent &&
                      fields && (
                        <InputFields
                          key={tab.key}
                          data={tabsContent[tab.key].map((column) =>
                            column.map((field) => ({
                              ...field,
                              ...fields[tab.key][field.fieldName],
                              onChange: (passedField: string, value: any) => (
                                handleInputChange(passedField, value, tab.key),
                                field.onChange && field.onChange(passedField, value)
                              ),
                            })),
                          )}
                        />
                      ),
                  )}
                </Box>
              </>
            ) : (
              content && (
                <Box className={styles.description}>
                  <InputFields
                    data={content.map((column) => [
                      ...column.map((field) => ({
                        ...field,
                        ...fields[field.fieldName],
                        onChange: (passedField: string, value: any) => (
                          handleInputChange(passedField, value), field.onChange && field.onChange(passedField, value)
                        ),
                      })),
                    ])}
                  />
                </Box>
              )
            )}

            <Box className={styles.bottomContainer}>
              {backButton && <Button onClick={handleOnClickBack}>{t(tPaths.itemContent.back)}</Button>}
              {editable && (
                <Box marginLeft="auto">
                  <Button color="secondary" onClick={handleOnClickSave}>
                    {addItem ? t(tPaths.itemContent.add) : t(tPaths.itemContent.save)}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      ) : (
        <CircularProgress />
      )}
    </Box>
  );
};

export default ItemContent;
